import { useEffect, useState } from 'react';
import { Alert, Box, TextField } from '@mui/material';
import Modal from '../../../components/atoms/modal/modal';
import Button from '../../../components/atoms/button';
import { createVehicleModel } from './functions';
import { VehicleModel } from './types';

type CreateModelProps = {
  brandId: number | '';
  brandName: string;
  open: boolean;
  onClose: () => void;
  onCreated: (model: VehicleModel) => void;
  initialName?: string;
};

const CreateModel = ({ brandId, brandName, open, onClose, onCreated, initialName }: CreateModelProps) => {
  const [name, setName] = useState(initialName ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName ?? '');
      setError(false);
    }
  }, [open, initialName]);

  const handleSave = async () => {
    if (brandId === '' || !name.trim()) {
      return;
    }

    setSaving(true);
    setError(false);

    try {
      onCreated(await createVehicleModel(brandId, name));
      onClose();
    } catch (saveError) {
      console.error('Error creating vehicle model', saveError);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create vehicle model"
      footer={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || brandId === '' || !name.trim()}>Save</Button>
        </Box>
      }
    >
      {error && <Alert severity="error">Unable to create vehicle model.</Alert>}
      <TextField
        label="Brand"
        value={brandName}
        disabled
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Model name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        size="small"
        fullWidth
        autoFocus
      />
    </Modal>
  );
};

export default CreateModel;
